import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Loader2, X, ArrowRight, ArrowLeft, Check, ShieldCheck, Users, Fuel, Cog, Lock, BadgeCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopTunnel({ slug, shop, vehicle, dark, search, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    start_date: search?.start_date || today, end_date: search?.end_date || today,
    start_time: search?.start_time || "09:00", end_time: search?.end_time || "18:00",
    client_name: "", client_email: "", client_phone: "",
  });
  const [selected, setSelected] = useState([]);
  const [quote, setQuote] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const fetchQuote = useCallback(() => {
    const pt = new URLSearchParams(window.location.search).get("pt") || "";
    axios.get(`${API}/public/shop/${slug}/quote`, { params: { vehicle_id: vehicle.id, start_date: form.start_date, end_date: form.end_date, ...(pt ? { pt } : {}) } })
      .then(({ data }) => {
        if (data && data.configured === false) {
          setQuote(null);
          setError("Tarif non configuré pour ces dates. Le loueur doit compléter sa grille tarifaire.");
        } else {
          setQuote(data); setError("");
        }
      })
      .catch((e) => { setQuote(null); setError(e.response?.data?.detail || "Dates invalides"); });
  }, [slug, vehicle.id, form.start_date, form.end_date]);
  useEffect(fetchQuote, [fetchQuote]);

  const payableOptions = (shop.options || []).filter((o) => o.selectable);
  const includedOptions = (shop.options || []).filter((o) => !o.selectable && !(o.fee || 0) && o.key.startsWith("prod_"));
  const _days = quote?.days || 0;
  const inDuration = (o) => (o.min_days == null || _days === 0 || _days >= o.min_days) && (o.max_days == null || _days === 0 || _days <= o.max_days);
  const isMandatory = (o) => o.required && (!(o.required_categories || []).length || (o.required_categories || []).includes(vehicle?.category)) && inDuration(o);
  const visibleOptions = payableOptions.filter(inDuration);
  const mandatoryKeys = payableOptions.filter(isMandatory).map((o) => o.key);
  // Les options obligatoires sont toujours incluses et non retirables
  useEffect(() => {
    if (!mandatoryKeys.length) return;
    setSelected((prev) => {
      const add = mandatoryKeys.filter((k) => !prev.includes(k));
      return add.length ? [...prev, ...add] : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mandatoryKeys.join(",")]);

  // Disponibilité produits à stock limité (blocage total en boutique)
  const [availability, setAvailability] = useState({});
  useEffect(() => {
    if (!form.start_date || !form.end_date) return;
    const pt = new URLSearchParams(window.location.search).get("pt") || "";
    axios.get(`${API}/public/shop/${slug}/product-availability`, { params: { start_date: form.start_date, end_date: form.end_date, ...(pt ? { pt } : {}) } })
      .then(({ data }) => {
        const avail = data.availability || {};
        setAvailability(avail);
        // Retire les options devenues épuisées après changement de dates
        setSelected((prev) => prev.filter((k) => !(avail[k] && avail[k].remaining <= 0)));
      })
      .catch(() => setAvailability({}));
  }, [slug, form.start_date, form.end_date]);
  const isSoldOut = (key) => availability[key] && availability[key].remaining <= 0;
  const hasProtection = !!(shop.deposit || shop.franchise || shop.franchise_buyback);
  const STEPS = useMemo(() => [
    { key: "dates", label: "Dates" },
    ...(payableOptions.length ? [{ key: "options", label: "Options" }] : []),
    ...(hasProtection ? [{ key: "protection", label: "Protection" }] : []),
    { key: "pay", label: "Paiement" },
  ], [payableOptions.length, hasProtection]);
  const cur = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const days = quote?.days || 1;
  const extras = selected.reduce((sum, k) => {
    if (k === "franchise_buyback") return sum + (shop.franchise_buyback?.price_per_day || 0) * days;
    const o = payableOptions.find((x) => x.key === k);
    if (!o) return sum;
    if (o.per !== "jour") return sum + (o.fee || 0);
    const raw = (o.fee || 0) * days;
    return sum + (o.price_cap != null && o.price_cap > 0 ? Math.min(raw, o.price_cap) : raw);
  }, 0);
  const total = quote ? Math.round((quote.total + extras) * 100) / 100 : null;
  const toggleOpt = (k) => {
    if (mandatoryKeys.includes(k)) return; // obligatoire : non retirable
    setSelected((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  };

  // Mode d'encaissement défini par le loueur (Paramètres → Paiements)
  const payCfg = shop.payment || {};
  const onlinePay = payCfg.online_enabled !== false && payCfg.collect_mode !== "on_site";
  const isDeposit = onlinePay && payCfg.collect_mode === "deposit";
  const depositAmount = isDeposit && total ? Math.max(1, Math.round(total * (payCfg.deposit_percent || 30)) / 100) : null;

  const pay = async () => {
    if (!form.client_name || !form.client_email || !form.client_phone) { setError("Renseignez vos coordonnées pour continuer."); return; }
    setPaying(true); setError("");
    try {
      const labels = selected.map((k) => k === "franchise_buyback" ? "Rachat de franchise" : payableOptions.find((o) => o.key === k)?.label).filter(Boolean);
      const common = {
        vehicle_id: vehicle.id, client_name: form.client_name, client_email: form.client_email, client_phone: form.client_phone,
        start_date: form.start_date, end_date: form.end_date,
        options: selected,
        custom_answers: { heure_depart: form.start_time, heure_retour: form.end_time, options: labels.join(", ") },
      };
      if (!onlinePay) {
        const { data } = await axios.post(`${API}/bookings`, common);
        window.location.href = `/confirmation/${data.booking.id}`;
        return;
      }
      const { data } = await axios.post(`${API}/checkout/create-session`, { ...common, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { setError(e.response?.data?.detail || "La réservation n'a pas pu démarrer."); setPaying(false); }
  };

  const panel = dark ? "bg-slate-900 border border-white/10 text-white" : "bg-white text-slate-900 border border-slate-200";
  const muted = dark ? "text-slate-400" : "text-slate-500";
  const input = dark
    ? "w-full h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-violet-400"
    : "w-full h-11 px-3 rounded-xl bg-white border border-slate-300 text-sm focus:outline-none focus:border-slate-900";
  const lbl = `text-[10px] font-bold uppercase tracking-wider mb-1 block ${muted}`;
  const primary = dark ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-90" : "bg-slate-900 text-white hover:bg-slate-700";
  const optCard = (active) => `w-full flex items-center justify-between gap-3 p-3.5 rounded-xl border text-left transition-all ${active ? (dark ? "border-violet-400 bg-violet-500/10" : "border-slate-900 bg-slate-50") : (dark ? "border-white/10 bg-white/[0.03] hover:border-white/25" : "border-slate-200 bg-white hover:border-slate-400")}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch" data-testid="shop-tunnel">
      <div className={`fixed inset-0 ${dark ? "bg-[#0A0F1E]/70" : "bg-white/50"} backdrop-blur-lg`} onClick={onClose} />
      <div className="relative w-full max-w-5xl mx-auto my-0 sm:my-6 flex flex-col lg:flex-row gap-4 p-3 sm:p-4 overflow-y-auto">
        {/* Vignette véhicule à gauche */}
        <div className={`lg:w-[340px] shrink-0 rounded-2xl overflow-hidden h-fit lg:sticky lg:top-0 ${panel}`} data-testid="tunnel-vehicle-card" style={{ animation: "jnSlideLeft .35s both" }}>
          <div className="aspect-[16/9] lg:aspect-[16/10]">
            {vehicle.photos?.[0]
              ? <img src={vehicle.photos[0]} alt="" className="w-full h-full object-cover" />
              : <div className={`w-full h-full ${dark ? "bg-white/5" : "bg-slate-100"}`} />}
          </div>
          <div className="p-4">
            <div className="text-base font-semibold">{vehicle.name || `${vehicle.brand} ${vehicle.model}`}</div>
            <div className="mt-1.5 flex flex-wrap gap-x-2.5 gap-y-1">
              {vehicle.seats ? <span className={`inline-flex items-center gap-1 text-[11px] ${muted}`}><Users className="h-3 w-3" />{vehicle.seats} places</span> : null}
              {vehicle.transmission ? <span className={`inline-flex items-center gap-1 text-[11px] ${muted}`}><Cog className="h-3 w-3" />{vehicle.transmission}</span> : null}
              {vehicle.fuel ? <span className={`inline-flex items-center gap-1 text-[11px] ${muted}`}><Fuel className="h-3 w-3" />{vehicle.fuel}</span> : null}
            </div>
            {quote && (
              <div className={`mt-3 rounded-xl p-3 text-sm ${dark ? "bg-white/5" : "bg-slate-50"}`} data-testid="tunnel-price">
                <div className="flex justify-between"><span className={muted}>{quote.daily_price} € × {days} j</span><span className="font-semibold">{quote.total} €</span></div>
                {extras > 0 && <div className="flex justify-between mt-1"><span className={muted}>Options</span><span className="font-semibold">+{Math.round(extras * 100) / 100} €</span></div>}
                <div className={`flex justify-between mt-2 pt-2 border-t ${dark ? "border-white/10" : "border-slate-200"}`}><span className="font-bold">Total</span><span className="font-bold text-lg" data-testid="tunnel-total">{total} €</span></div>
              </div>
            )}
          </div>
        </div>

        {/* Étapes à droite */}
        <div className={`flex-1 rounded-2xl p-5 sm:p-7 relative ${panel}`} style={{ animation: "jnSlideRight .35s both" }}>
          <button onClick={onClose} className={`absolute top-4 right-4 p-2 rounded-full ${dark ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-100 text-slate-400"}`} data-testid="tunnel-close"><X className="h-4 w-4" /></button>
          <div className="flex items-center gap-1.5 mb-6">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                {i > 0 && <div className={`h-px w-6 ${i <= step ? "bg-emerald-400" : dark ? "bg-white/10" : "bg-slate-200"}`} />}
                <button onClick={() => i < step && setStep(i)} className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${i === step ? "bg-emerald-500 text-white" : i < step ? "bg-emerald-500/15 text-emerald-500" : dark ? "bg-white/5 text-slate-500" : "bg-slate-100 text-slate-400"}`} data-testid={`tunnel-step-${s.key}`}>
                  {i < step ? <Check className="h-3 w-3 inline" /> : null} {s.label}
                </button>
              </React.Fragment>
            ))}
          </div>

          {cur.key === "dates" && (
            <div data-testid="tunnel-dates">
              <h3 className="text-lg font-semibold mb-4">Vos dates de location</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><span className={lbl}>Départ</span><input type="date" min={today} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={input} data-testid="tunnel-start-date" /></div>
                <div><span className={lbl}>Heure</span><input type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className={input} /></div>
                <div><span className={lbl}>Retour</span><input type="date" min={form.start_date} value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={input} data-testid="tunnel-end-date" /></div>
                <div><span className={lbl}>Heure</span><input type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className={input} /></div>
              </div>
            </div>
          )}

          {cur.key === "options" && (
            <div data-testid="tunnel-options">
              <h3 className="text-lg font-semibold mb-1">Vos options</h3>
              <p className={`text-xs mb-4 ${muted}`}>Ajoutez ce dont vous avez besoin — le total se met à jour.</p>
              <div className="space-y-2">
                {visibleOptions.map((o) => {
                  const soldOut = isSoldOut(o.key);
                  const mandatory = mandatoryKeys.includes(o.key);
                  const locked = soldOut && !mandatory;
                  return (
                    <button key={o.key} onClick={() => !locked && toggleOpt(o.key)} disabled={locked || mandatory}
                      className={optCard(selected.includes(o.key) || mandatory)} style={locked ? { opacity: 0.5, cursor: "not-allowed" } : mandatory ? { cursor: "default" } : undefined}
                      data-testid={`tunnel-option-${o.key}`}>
                      <div>
                        <div className="text-sm font-semibold">
                          {o.label}
                          {mandatory && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-testid={`tunnel-option-mandatory-${o.key}`}>Obligatoire</span>}
                          {soldOut && !mandatory && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold" data-testid={`tunnel-option-soldout-${o.key}`}>Épuisé</span>}
                        </div>
                        {o.detail && <div className={`text-[11px] ${muted}`}>{o.detail}</div>}
                        {!soldOut && availability[o.key] && availability[o.key].remaining <= 2 && (
                          <div className="text-[10px] font-bold text-amber-500">Plus que {availability[o.key].remaining} disponible{availability[o.key].remaining > 1 ? "s" : ""}</div>
                        )}
                      </div>
                      <div className="text-sm font-bold shrink-0">{o.fee ? `+${o.fee} €${o.per === "jour" ? "/jour" : ""}${o.per === "jour" && o.price_cap != null && o.price_cap > 0 ? ` (max ${o.price_cap} €)` : ""}` : "Inclus"}</div>
                    </button>
                  );
                })}
                {includedOptions.map((o) => (
                  <div key={o.key} className={optCard(false)} style={{ cursor: "default" }} data-testid={`tunnel-option-${o.key}`}>
                    <div>
                      <div className="text-sm font-semibold">{o.label}</div>
                      {o.detail && o.detail !== "Inclus" && <div className={`text-[11px] ${muted}`}>{o.detail}</div>}
                    </div>
                    <div className="text-sm font-bold shrink-0 text-emerald-600">Inclus</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cur.key === "protection" && (
            <div data-testid="tunnel-protection">
              <h3 className="text-lg font-semibold mb-1">Caution & protection</h3>
              <p className={`text-xs mb-4 ${muted}`}>En toute transparence, avant de payer.</p>
              <div className="space-y-2">
                {shop.deposit ? (
                  <div className={optCard(false)} style={{ cursor: "default" }}>
                    <div><div className="text-sm font-semibold">Caution</div><div className={`text-[11px] ${muted}`}>Empreinte bancaire, restituée au retour du véhicule.</div></div>
                    <div className="text-sm font-bold">{shop.deposit} €</div>
                  </div>
                ) : null}
                {shop.franchise ? (
                  <div className={optCard(false)} style={{ cursor: "default" }}>
                    <div><div className="text-sm font-semibold">Franchise en cas de sinistre</div><div className={`text-[11px] ${muted}`}>Montant maximal restant à votre charge.</div></div>
                    <div className="text-sm font-bold">{shop.franchise} €</div>
                  </div>
                ) : null}
                {shop.franchise_buyback ? (
                  <button onClick={() => toggleOpt("franchise_buyback")} className={optCard(selected.includes("franchise_buyback"))} data-testid="tunnel-buyback">
                    <div className="flex items-start gap-2">
                      <ShieldCheck className={`h-4 w-4 mt-0.5 ${selected.includes("franchise_buyback") ? "text-emerald-500" : muted}`} />
                      <div><div className="text-sm font-semibold">Rachat de franchise</div><div className={`text-[11px] ${muted}`}>Roulez l'esprit tranquille : franchise réduite à 0 €.</div></div>
                    </div>
                    <div className="text-sm font-bold shrink-0">+{shop.franchise_buyback.price_per_day} €/j</div>
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {cur.key === "pay" && (
            <div data-testid="tunnel-pay">
              <h3 className="text-lg font-semibold mb-4">Vos coordonnées</h3>
              <div className="space-y-3">
                <div><span className={lbl}>Nom complet</span><input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} placeholder="Jean Dupont" className={input} data-testid="tunnel-client-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><span className={lbl}>Email</span><input type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} placeholder="jean@mail.fr" className={input} data-testid="tunnel-client-email" /></div>
                  <div><span className={lbl}>Téléphone</span><input value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} placeholder="06 12 34 56 78" className={input} data-testid="tunnel-client-phone" /></div>
                </div>
              </div>
              <div className={`mt-4 rounded-xl p-3.5 text-xs ${dark ? "bg-white/5" : "bg-slate-50"}`} data-testid="tunnel-recap">
                <div className="font-bold mb-1">Récapitulatif</div>
                <div className={muted}>{vehicle.name || `${vehicle.brand} ${vehicle.model}`} · du {form.start_date} {form.start_time} au {form.end_date} {form.end_time}</div>
                {selected.length > 0 && <div className={muted}>Options : {selected.map((k) => k === "franchise_buyback" ? "Rachat de franchise" : payableOptions.find((o) => o.key === k)?.label).join(", ")}</div>}
                {isDeposit && depositAmount && total && (
                  <div className="mt-1.5 font-semibold" data-testid="tunnel-deposit-info">
                    Acompte à régler maintenant : {depositAmount} € — solde de {Math.round((total - depositAmount) * 100) / 100} € sur place.
                  </div>
                )}
                {!onlinePay && (
                  <div className="mt-1.5 font-semibold" data-testid="tunnel-onsite-info">
                    Aucun paiement en ligne : vous réglez la totalité ({total} €) sur place.
                  </div>
                )}
              </div>
              <div className={`mt-3 flex flex-wrap gap-x-4 gap-y-1.5`} data-testid="tunnel-trust">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold ${muted}`}><Lock className="h-3 w-3 text-emerald-500" /> Paiement chiffré — géré par Stripe</span>
                {shop.verified && <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold ${muted}`}><BadgeCheck className="h-3 w-3 text-emerald-500" /> Loueur vérifié junago.com</span>}
                {shop.insurance && <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold ${muted}`}><ShieldCheck className="h-3 w-3 text-emerald-500" /> Véhicule assuré par {shop.insurance.insurer}</span>}
              </div>
            </div>
          )}

          {error && <div className="mt-3 text-xs font-medium text-red-500" data-testid="tunnel-error">{error}</div>}

          <div className="mt-6 flex items-center justify-between">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} className={`inline-flex items-center gap-1.5 text-xs font-semibold ${muted} hover:opacity-70`} data-testid="tunnel-back"><ArrowLeft className="h-3.5 w-3.5" /> Retour</button>
            ) : <span />}
            {isLast ? (
              <button onClick={pay} disabled={paying || !quote} className={`inline-flex items-center gap-2 px-6 h-12 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 ${primary}`} data-testid="tunnel-pay-btn">
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {paying ? "Redirection…"
                  : !onlinePay ? "Confirmer la réservation"
                  : isDeposit && depositAmount ? `Payer l'acompte de ${depositAmount} €`
                  : total ? `Payer ${total} € et réserver` : "Payer et réserver"}
              </button>
            ) : (
              <button onClick={() => quote && setStep(step + 1)} disabled={!quote} className={`inline-flex items-center gap-2 px-6 h-12 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 ${primary}`} data-testid="tunnel-next">
                Continuer <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes jnSlideLeft{from{opacity:0;transform:translateX(-24px)}to{opacity:1;transform:none}}@keyframes jnSlideRight{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}
